import React from "react";
import {
	ExclamationCircleOutlined,
	InfoCircleOutlined,
	WarningOutlined,
} from "@ant-design/icons";
import { currencySymbol } from "../../../utils/formatters";
import styles from "./RegisterOrderForm.module.css";

type PriceSummaryProps = {
	/** The order's own currency — every figure here is in it, and only in it. */
	currencyId?: number;
	/** The package value as typed; an empty string while the field is empty. */
	orderPrice: number | string;
	/** Null while this order's tariff bands are loading or failed to load — never a guess. */
	transportPrice: number | null;
	tariffError: boolean;
	/**
	 * A payment return does not follow the "buyer pays, Postman keeps transport" story, so its note
	 * states the balance rule the save enforces instead.
	 */
	returnable: boolean;
	strings: any;
};

const amountFormat = new Intl.NumberFormat("en-US", {
	minimumFractionDigits: 2,
	maximumFractionDigits: 2,
});

/** Replaces `{name}` placeholders in a locale string with bold values. */
const fillTemplate = (
	template: string | undefined,
	values: Record<string, React.ReactNode>
) =>
	(template ?? "").split(/\{(\w+)\}/g).map((part, index) =>
		// split() with a capture group alternates plain text and placeholder names.
		index % 2 === 1 ? (
			<strong key={index}>{values[part]}</strong>
		) : (
			<React.Fragment key={index}>{part}</React.Fragment>
		)
	);

/**
 * Read-only breakdown of what the order is worth to the seller.
 *
 * Transport cost and "Vlera për barazim" used to be input boxes, which read as something to fill
 * in. Here they are plain figures — value, minus transport, equals settlement — and the rows say
 * that on their own. A note is added only when there is something to act on: tariffs that failed
 * to load, the balance rule of a payment return, or a value that does not cover the transport.
 *
 * The arithmetic is the page's own (`orderPrice - transportPrice`); this only presents it.
 */
const PriceSummary = ({
	currencyId,
	orderPrice,
	transportPrice,
	tariffError,
	returnable,
	strings,
}: PriceSummaryProps) => {
	const money = (amount: number) =>
		`${amountFormat.format(amount)} ${currencySymbol(currencyId)}`;

	const hasValue = orderPrice !== "" && orderPrice !== null;
	const value = Number(orderPrice) || 0;
	const settlement = transportPrice === null ? null : value - transportPrice;
	const negative = hasValue && settlement !== null && settlement < 0;
	const pending = (
		<span className={styles.summaryPending}>
			{tariffError ? "—" : strings?.tariffLoadingHint}
		</span>
	);

	const renderNote = () => {
		if (tariffError) {
			return (
				<p
					className={`${styles.summaryNote} ${styles.summaryNoteError}`}
				>
					<ExclamationCircleOutlined
						className={styles.summaryNoteIcon}
					/>
					<span>{strings?.tariffErrorHint}</span>
				</p>
			);
		}
		if (returnable) {
			return (
				<p className={styles.summaryNote}>
					<InfoCircleOutlined className={styles.summaryNoteIcon} />
					<span>{strings?.summaryReturnableNote}</span>
				</p>
			);
		}
		if (negative && settlement !== null) {
			return (
				<p
					className={`${styles.summaryNote} ${styles.summaryNoteWarning}`}
				>
					<WarningOutlined className={styles.summaryNoteIcon} />
					<span>
						{fillTemplate(strings?.summaryNegativeNote, {
							amount: money(Math.abs(settlement)),
						})}
					</span>
				</p>
			);
		}
		return null;
	};

	return (
		<section className={styles.summary}>
			<h2 className={styles.summaryTitle}>{strings?.summaryTitle}</h2>
			<dl className={styles.summaryRows}>
				<div className={styles.summaryRow}>
					<dt>{strings?.packageValueLabel}</dt>
					<dd>{hasValue ? money(value) : "—"}</dd>
				</div>
				<div className={styles.summaryRow}>
					<dt>{strings?.transportCostLabel}</dt>
					<dd>
						{transportPrice === null
							? pending
							: `− ${money(transportPrice)}`}
					</dd>
				</div>
				<div
					className={`${styles.summaryRow} ${styles.summaryTotal} ${
						negative ? styles.summaryTotalNegative : ""
					}`}
				>
					<dt>{strings?.profitLabel}</dt>
					<dd>
						{settlement === null
							? pending
							: hasValue
							? money(settlement)
							: "—"}
					</dd>
				</div>
			</dl>
			{renderNote()}
		</section>
	);
};

export default PriceSummary;
